/**
 * Ndure search SKU helpers
 *
 * Catalog SKUs are STYLE:COLOR:SIZE (e.g. W-SN-MDN-0006:BLACK:36).
 * Colors are separate products. Shopify tokenizes on ":", so Style:Color
 * queries flood the catalog; theme code should search by STYLE then filter
 * with these helpers.
 *
 * Public API: window.NdureSearchSku
 * Debug: add ?debug_sku=1 to the URL to log rewrite/filter actions.
 */
(function (window) {
  'use strict';

  var STYLE_LIKE_RE = /^[A-Z0-9]+(?:-[A-Z0-9]+)+$/i;
  var EXACT_SKU_PARAM = 'exact_sku';
  var PREFIX_PARAM = 'options[prefix]';
  var DEBUG_PARAM = 'debug_sku';

  function isDebugEnabled() {
    try {
      return new URLSearchParams(window.location.search).get(DEBUG_PARAM) === '1';
    } catch (e) {
      return false;
    }
  }

  function debugLog() {
    if (!isDebugEnabled() || typeof console === 'undefined' || !console.log) return;
    var args = ['[NdureSearchSku]'].concat(Array.prototype.slice.call(arguments));
    console.log.apply(console, args);
  }

  function normalize(value) {
    return String(value == null ? '' : value)
      .trim()
      .replace(/\s+/g, ' ')
      .toUpperCase();
  }

  /**
   * Style codes look like W-SN-MDN-0006 or ND-CU-0089 (hyphenated alphanumerics).
   * Rejects bare words like BLACK so keyword searches are not rewritten.
   */
  function isStyleLike(segment) {
    var s = normalize(segment);
    if (!s || s.indexOf(' ') !== -1) return false;
    return STYLE_LIKE_RE.test(s);
  }

  /**
   * @param {string} raw
   * @returns {{
   *   kind: 'keyword' | 'style' | 'style_color',
   *   style: string | null,
   *   exact: string | null,
   *   original: string
   * }}
   */
  function parseSkuQuery(raw) {
    var original = String(raw == null ? '' : raw).trim().replace(/\s+/g, ' ');
    var empty = {
      kind: 'keyword',
      style: null,
      exact: null,
      original: original,
    };

    if (!original) return empty;

    var colonIndex = original.indexOf(':');
    if (colonIndex === -1) {
      if (isStyleLike(original)) {
        return {
          kind: 'style',
          style: normalize(original),
          exact: null,
          original: original,
        };
      }
      return empty;
    }

    var stylePart = original.slice(0, colonIndex).trim();
    var remainder = original.slice(colonIndex + 1).trim();

    if (!isStyleLike(stylePart) || !remainder) {
      return empty;
    }

    var style = normalize(stylePart);
    var exact = normalize(stylePart + ':' + remainder);

    return {
      kind: 'style_color',
      style: style,
      exact: exact,
      original: original,
    };
  }

  /**
   * True if a single variant SKU matches the query target.
   * STYLE matches STYLE and STYLE:…
   * STYLE:COLOR matches STYLE:COLOR and STYLE:COLOR:…
   */
  function skuMatches(sku, exact) {
    var s = normalize(sku);
    var q = normalize(exact);
    if (!s || !q) return false;
    return s === q || s.indexOf(q + ':') === 0;
  }

  /**
   * @param {string} dataSkus comma-separated variant SKUs from data-skus
   * @param {string} exact filter target
   */
  function productSkusMatch(dataSkus, exact) {
    if (!exact) return false;
    var list = String(dataSkus == null ? '' : dataSkus).split(',');
    for (var i = 0; i < list.length; i++) {
      if (skuMatches(list[i], exact)) return true;
    }
    return false;
  }

  /**
   * Build /search URL that asks Shopify for STYLE only and carries exact_sku
   * for client-side filtering.
   *
   * @param {{ style: string, exact: string, searchPath?: string, prefix?: string }} opts
   * @returns {string}
   */
  function buildSearchUrl(opts) {
    var style = normalize(opts && opts.style);
    var exact = normalize(opts && opts.exact);
    var searchPath = (opts && opts.searchPath) || (window.routes && window.routes.search_url) || '/search';
    var prefix = opts && opts.prefix != null ? opts.prefix : 'last';

    if (!style || !exact) {
      throw new Error('NdureSearchSku.buildSearchUrl requires style and exact');
    }

    var params = new URLSearchParams();
    params.set('q', style);
    params.set(EXACT_SKU_PARAM, exact);
    if (prefix !== '' && prefix != null) {
      params.set(PREFIX_PARAM, prefix);
    }

    return searchPath + '?' + params.toString();
  }

  /**
   * Read filter target from the current location.
   * Prefers exact_sku; falls back to parsing q when it is Style:Color.
   *
   * @param {string} [href]
   * @returns {{
   *   exact: string | null,
   *   style: string | null,
   *   q: string,
   *   fromExactParam: boolean,
   *   needsRewrite: boolean,
   *   parsed: ReturnType<typeof parseSkuQuery>
   * }}
   */
  function getExactFromLocation(href) {
    var url;
    try {
      url = new URL(href || window.location.href, window.location.origin);
    } catch (e) {
      url = new URL(window.location.href);
    }

    var q = url.searchParams.get('q') || '';
    var exactParam = url.searchParams.get(EXACT_SKU_PARAM);
    var parsed = parseSkuQuery(exactParam || q);

    if (exactParam) {
      var fromParam = parseSkuQuery(exactParam);
      return {
        exact: fromParam.exact || normalize(exactParam) || null,
        style: fromParam.style || normalize(q) || null,
        q: q,
        fromExactParam: true,
        needsRewrite: false,
        parsed: fromParam.kind === 'keyword' && exactParam ? parseSkuQuery(q) : fromParam,
      };
    }

    var needsRewrite = parsed.kind === 'style_color';

    return {
      exact: needsRewrite ? parsed.exact : null,
      style: needsRewrite ? parsed.style : parsed.kind === 'style' ? parsed.style : null,
      q: q,
      fromExactParam: false,
      needsRewrite: needsRewrite,
      parsed: parsed,
    };
  }

  /**
   * If the current URL is a raw Style:Color search, return the rewrite URL;
   * otherwise null. Used to avoid rewrite loops when exact_sku is already set.
   */
  function getRewriteUrlIfNeeded(href) {
    var info = getExactFromLocation(href);
    if (!info.needsRewrite || !info.style || !info.exact) return null;
    return buildSearchUrl({ style: info.style, exact: info.exact });
  }

  function isSearchPath(pathname) {
    var searchPath = (window.routes && window.routes.search_url) || '/search';
    var path = pathname || window.location.pathname;
    return path === searchPath || path.indexOf(searchPath + '/') === 0;
  }

  function isSearchForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    var action = form.getAttribute('action') || '';
    var searchPath = (window.routes && window.routes.search_url) || '/search';
    if (!action || action === searchPath) return true;
    try {
      var actionPath = new URL(action, window.location.origin).pathname;
      return actionPath === searchPath;
    } catch (e) {
      return action.indexOf('/search') !== -1;
    }
  }

  /**
   * Navigate to style-only search with exact_sku when query is Style:Color.
   * @returns {boolean} true if navigation was started (caller should preventDefault)
   */
  function tryRewriteNavigation(query, options) {
    var parsed = parseSkuQuery(query);
    if (parsed.kind !== 'style_color' || !parsed.style || !parsed.exact) return false;
    var url = buildSearchUrl({ style: parsed.style, exact: parsed.exact });
    debugLog('rewrite navigation', query, '→', url);
    var replace = options && options.replace;
    if (replace) {
      window.location.replace(url);
    } else {
      window.location.assign(url);
    }
    return true;
  }

  /**
   * Show the user's Style:Color query in search inputs when exact_sku is present.
   */
  function syncSearchInputsFromExactSku() {
    var info = getExactFromLocation();
    if (!info.fromExactParam) return;

    var display = new URLSearchParams(window.location.search).get(EXACT_SKU_PARAM);
    if (!display) return;

    var inputs = document.querySelectorAll(
      'form[action*="/search"] input[name="q"], form[role="search"] input[name="q"], #Search-In-Template'
    );
    for (var i = 0; i < inputs.length; i++) {
      inputs[i].value = display;
    }
  }

  /**
   * Deep-link: /search?q=STYLE:COLOR → /search?q=STYLE&exact_sku=STYLE:COLOR
   * @returns {boolean} true if a redirect was started
   */
  function bootDeepLinkRewrite() {
    if (!isSearchPath()) {
      syncSearchInputsFromExactSku();
      return false;
    }

    var rewriteUrl = getRewriteUrlIfNeeded();
    if (rewriteUrl) {
      var nextHref = new URL(rewriteUrl, window.location.origin).href;
      if (nextHref !== window.location.href) {
        debugLog('deep-link rewrite', window.location.href, '→', rewriteUrl);
        window.location.replace(rewriteUrl);
        return true;
      }
    }

    syncSearchInputsFromExactSku();
    return false;
  }

  function onDocumentSubmit(event) {
    var form = event.target;
    if (!isSearchForm(form)) return;

    var predictiveRoot = form.closest('predictive-search');
    if (predictiveRoot && predictiveRoot.querySelector('[aria-selected="true"] a')) {
      return;
    }

    var input = form.querySelector('input[name="q"], input[type="search"]');
    var query = input ? String(input.value || '').trim() : '';
    if (!query) return;

    if (tryRewriteNavigation(query)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  function initPhase2() {
    document.addEventListener('submit', onDocumentSubmit, true);
    if (bootDeepLinkRewrite()) return;
    if (isSearchPath()) {
      initSearchResultsFilter();
    }
  }

  var filterObserver = null;
  var filterApplying = false;

  function formatResultCount(count) {
    return count === 1 ? '1 result' : count + ' results';
  }

  function updateResultCountUI(count) {
    var label = formatResultCount(count);
    ['ProductCount', 'ProductCountDesktop'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.textContent = label;
    });
  }

  function appendExactSkuToParams(searchParams) {
    var exact = new URLSearchParams(window.location.search).get(EXACT_SKU_PARAM);
    if (!exact) return searchParams || '';
    var params = new URLSearchParams(searchParams || '');
    if (!params.get(EXACT_SKU_PARAM)) {
      params.set(EXACT_SKU_PARAM, exact);
    }
    return params.toString();
  }

  function ensureExactSkuHiddenInputs() {
    var exact = new URLSearchParams(window.location.search).get(EXACT_SKU_PARAM);
    var forms = document.querySelectorAll(
      'form#FacetFiltersForm, form#FacetFiltersFormMobile, form#FacetSortForm, form#FacetSortDrawerForm, form#FacetFiltersPillsForm, form.facets-vertical-form'
    );

    for (var i = 0; i < forms.length; i++) {
      var form = forms[i];
      var input = form.querySelector('input[name="' + EXACT_SKU_PARAM + '"]');
      if (!exact) {
        if (input && input.parentNode) input.parentNode.removeChild(input);
        continue;
      }
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = EXACT_SKU_PARAM;
        form.appendChild(input);
      }
      input.value = exact;
    }
  }

  function hideInfiniteScroll() {
    var wrap = document.querySelector('.infinite__scroll--button-wrap');
    if (wrap) wrap.style.display = 'none';
  }

  function ensureSkuEmptyState(terms) {
    var container = document.getElementById('ProductGridContainer');
    if (!container) return;

    var existing = document.getElementById('SkuExactEmptyState');
    if (existing) return;

    var empty = document.createElement('div');
    empty.id = 'SkuExactEmptyState';
    empty.className = 'template-search__results collection collection--empty page-width';
    empty.setAttribute('data-sku-empty', 'true');
    empty.innerHTML =
      '<div class="title-wrapper center">' +
      '<h2 class="title title--primary" role="status"></h2>' +
      '</div>';
    var heading = empty.querySelector('h2');
    heading.textContent =
      'No results found for “' + (terms || '') + '”. Check the spelling or use a different word or phrase.';
    container.appendChild(empty);
  }

  function clearSkuEmptyState() {
    var existing = document.getElementById('SkuExactEmptyState');
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
  }

  /**
   * Filter (Style:Color) or promote-sort (style-only) product grid cards by SKU.
   * @returns {{ matched: number, mode: 'filter' | 'promote' | 'none' }}
   */
  function filterAndRankBySku() {
    var grid = document.getElementById('product-grid');
    if (!grid) return { matched: 0, mode: 'none' };

    var info = getExactFromLocation();
    var exactFilter = info.exact;
    var items = Array.prototype.slice.call(grid.querySelectorAll('.grid__item'));

    if (exactFilter) {
      var matches = [];
      var removals = [];
      for (var i = 0; i < items.length; i++) {
        if (productSkusMatch(items[i].getAttribute('data-skus') || '', exactFilter)) {
          matches.push(items[i]);
        } else {
          removals.push(items[i]);
        }
      }

      for (var r = 0; r < removals.length; r++) {
        if (removals[r].parentNode) removals[r].parentNode.removeChild(removals[r]);
      }
      for (var m = 0; m < matches.length; m++) {
        grid.appendChild(matches[m]);
      }

      var displayTerms =
        new URLSearchParams(window.location.search).get(EXACT_SKU_PARAM) || exactFilter;
      updateResultCountUI(matches.length);
      hideInfiniteScroll();
      debugLog('filter exact', exactFilter, 'matched', matches.length, 'removed', removals.length);

      if (matches.length === 0) {
        ensureSkuEmptyState(displayTerms);
      } else {
        clearSkuEmptyState();
      }

      return { matched: matches.length, mode: 'filter' };
    }

    // Style-only / keyword: promote SKU prefix matches to the front (legacy behavior).
    var promote = null;
    if (info.parsed && info.parsed.kind === 'style' && info.parsed.style) {
      promote = info.parsed.style;
    } else if (info.q && isStyleLike(info.q)) {
      promote = normalize(info.q);
    }

    if (!promote || !items.length) {
      return { matched: items.length, mode: 'none' };
    }

    items.sort(function (a, b) {
      var aMatch = productSkusMatch(a.getAttribute('data-skus') || '', promote);
      var bMatch = productSkusMatch(b.getAttribute('data-skus') || '', promote);
      if (aMatch && !bMatch) return -1;
      if (!aMatch && bMatch) return 1;
      return 0;
    });
    items.forEach(function (item) {
      grid.appendChild(item);
    });

    debugLog('promote style', promote, 'items', items.length);
    return { matched: items.length, mode: 'promote' };
  }

  function initSearchResultsFilter() {
    if (!isSearchPath()) return;

    function apply() {
      if (filterApplying) return;
      filterApplying = true;
      try {
        ensureExactSkuHiddenInputs();
        filterAndRankBySku();
        syncSearchInputsFromExactSku();
      } finally {
        // Allow observer to settle after our DOM writes.
        setTimeout(function () {
          filterApplying = false;
        }, 0);
      }
    }

    apply();

    var parentContainer = document.getElementById('ProductGridContainer');
    if (!parentContainer || filterObserver) return;
    if (typeof MutationObserver === 'undefined') return;

    var debounceTimer;
    filterObserver = new MutationObserver(function () {
      if (filterApplying) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(apply, 50);
    });
    filterObserver.observe(parentContainer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPhase2);
  } else {
    initPhase2();
  }

  window.NdureSearchSku = {
    EXACT_SKU_PARAM: EXACT_SKU_PARAM,
    DEBUG_PARAM: DEBUG_PARAM,
    normalize: normalize,
    isStyleLike: isStyleLike,
    parseSkuQuery: parseSkuQuery,
    skuMatches: skuMatches,
    productSkusMatch: productSkusMatch,
    buildSearchUrl: buildSearchUrl,
    getExactFromLocation: getExactFromLocation,
    getRewriteUrlIfNeeded: getRewriteUrlIfNeeded,
    tryRewriteNavigation: tryRewriteNavigation,
    syncSearchInputsFromExactSku: syncSearchInputsFromExactSku,
    bootDeepLinkRewrite: bootDeepLinkRewrite,
    filterAndRankBySku: filterAndRankBySku,
    initSearchResultsFilter: initSearchResultsFilter,
    appendExactSkuToParams: appendExactSkuToParams,
    ensureExactSkuHiddenInputs: ensureExactSkuHiddenInputs,
    isDebugEnabled: isDebugEnabled,
  };
})(window);
